// React
import { useCallback } from 'react'

// Interfaces
import { ISharedCertificateFormTableProps } from './interfaces'
import { ISharedSupportDocument } from 'interfaces/shared'

// Antd
import { ColumnsType } from 'antd/es/table'
import { CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons'

// i18n
import { useTranslation } from 'react-i18next'

// Components
import { BaseImage, BaseLabelStatus, BaseTableAction, BaseTableBody } from 'components'

// Mobx
import { observer } from 'mobx-react'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

const SharedCertificateFormTable = observer(({ data, onUpload, onDelete, onDownload }: ISharedCertificateFormTableProps) => {
  // Hook
  const { t } = useTranslation()

  // Common State
  const columns: ColumnsType<ISharedSupportDocument> = [
    {
      key: 'isMandatory',
      title: t('shared.certificateForm.table.mandatory'),
      dataIndex: 'isMandatory',
      align: 'center',
      render: (_, record) => {
        return record.isMandatory ? (
          <CheckCircleOutlined style={{ color: 'green' }} />
        ) : (
          <CloseCircleOutlined style={{ color: 'red' }} />
        )
      },
    },
    {
      key: 'categoryName',
      title: t('shared.certificateForm.table.category'),
      dataIndex: 'categoryName',
    },
    {
      key: 'programTypeName',
      title: t('shared.certificateForm.table.programType'),
      dataIndex: 'programTypeName',
    },
    {
      key: 'type',
      title: t('shared.certificateForm.table.jenisPrasyarat'),
      dataIndex: 'type',
    },
    {
      key: 'documentProofName',
      title: t('shared.certificateForm.table.documentProof'),
      dataIndex: 'documentProofName',
    },
    {
      key: 'point',
      title: t('shared.certificateForm.table.point'),
      dataIndex: 'point',
    },
    {
      key: 'userOriginalFileName',
      title: t('shared.certificateForm.table.document'),
      dataIndex: 'userOriginalFileName',
      render: (_, record) => {
        return record.userFileBlobId ? (
          <div onClick={() => onDownload(record.userFileBlobId!, record.userOriginalFileName!)}>
            <div className="flex items-center gap-2 cursor-pointer text-primary font-medium">
              {record.userOriginalFileName}
              <BaseImage isIcon isSvg icon="back-office/icon-download-primary" />
            </div>
          </div>
        ) : (
          <>-</>
        )
      },
    },
    {
      key: 'status',
      title: t('shared.certificateForm.table.status'),
      dataIndex: 'status',
      render: (_, record) => {
        return <BaseLabelStatus isUserStatus value={record.status} />
      },
    },
    {
      key: 'notes',
      title: t('shared.certificateForm.table.notes'),
      dataIndex: 'notes',
      render: (_, record) => {
        return <p>{record.notes || '-'}</p>
      },
    },
    {
      key: 'action',
      title: t('app.table.action'),
      dataIndex: '',
      align: 'center',
      render: (_, record) => {
        const anotherAction = [
          {
            label: 'shared.certificateForm.action.uploadDocument',
            isUpload: true,
          },
          {
            label: record.fileBlobId! ? 'shared.certificateForm.action.downloadTemplate' : '',
            onClick: () => onDownload(record.fileBlobId!, record.originalFileName!, { isDownloadTemplate: true }),
          },
          {
            label: record.userFileBlobId! ? 'shared.certificateForm.action.deleteDocument' : '',
            onClick: () => onDelete(record.id!),
          },
        ].filter(action => action.label)

        return (
          <div className="flex justify-center text-center-items-center gap-5">
            <BaseTableAction
              isDelete={false}
              isShow={false}
              isEdit={false}
              anotherAction={anotherAction}
              onUpload={file => onUpload(record.id!, file)}
            />

            {/* Loader */}
            {record.isActionLoading && <SyncOutlined spin />}
          </div>
        )
      },
    },
  ]

  /**
   * @description Map preconditions form
   *
   * @return {ISharedSupportDocument[]} ISharedSupportDocument[]
   */
  const mapPreconditions = useCallback((): ISharedSupportDocument[] => {
    const supportDocuments = cloneDeep(data) as ISharedSupportDocument[]
    return supportDocuments.map((supportDocument, supportDocumentIndex) => {
      if (supportDocumentIndex !== 0) {
        if (supportDocument.categoryName === supportDocuments[supportDocumentIndex - 1].categoryName)
          supportDocument.categoryName = ''
        if (supportDocument.programTypeName === supportDocuments[supportDocumentIndex - 1].programTypeName)
          supportDocument.programTypeName = ''
      }

      return supportDocument
    })
  }, [data])

  return (
    <div className="mb-4">
      <BaseTableBody
        columns={columns}
        dataSource={[...mapPreconditions()]}
        pagination={false}
        rowKey="id"
        className="custom-table font-normal"
        rowClassName={record =>
          (!record.userOriginalFileName || !record.userFileBlobId) && record.isMandatory ? 'row-required' : ''
        }
        isBgPrimaryHeader
      />
    </div>
  )
})

export { SharedCertificateFormTable }
